import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts'
import {
  Container,
  BigCard,
  MyProcessContainer,
  BigContainer,
  TopContainer,
  ChartContainer,
  MyProcessBottomContainer,
  MyProcessList,
  InventoryItem,
  InventoryItemLabel,
  InventoryItemValue,
  InventoryWarning,
  InventoryList,
  InventoryListContainer,
  InventoryHeader,
  InventoryAction,
} from './styled'
import { P, SubtitleBold } from 'views/components/UI/Text'
import Select from 'views/components/UI/Select'
import { Button } from 'views/components/UI/Buttons'
import Doughnut from 'views/components/Graphics/Doughnut'
import { platformAdminRoutes } from 'router/routes'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { useFetchAllProcesses } from 'hooks/process/useGetAllProcessCurrentCompany/useGetAllProcessCurrentCompany'
import { useGetResumeProcess } from 'hooks/process/useGetResumeProcess/useGetResumeProcess'
import { ItemDraftProcesses, ItemSendedProcess } from './components'
import { cleanProcess } from 'ducks/createProcess'
import { useNotification } from 'lib/context/notification.context'
import { useFinishProcess } from 'hooks/process/useFinishProcess/useFinishProcess'
import { getProcessDetail } from 'services/common/process/createProcess/create-process-service'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { userSelector } from 'ducks/user'
import { Subtitle } from 'views/components/UI/StyledComponents/styled.components'
import dayjs from 'dayjs'
import fetchActiveInventories, { ActiveInventory } from './services/fetch-active-inventories'

const HomePlatformAdministrator = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { companyId } = useSelector(userSelector)
  const role = useSelector((state: any) => state.auth.role)
  const { getError, getWarning } = useNotification()
  const { allProcess, fetchAllProcesses, loadingAllProcesses } = useFetchAllProcesses()

  const { handleFinishProcess } = useFinishProcess()

  const { loadingDetailProcess, processInfo, fetchProcessData, fetchTestsByCompany, testsByCompany } =
    useGetResumeProcess()
  const [process, setProcess] = useState<number | ''>('')
  const [loadingFinishProcess, setLoadingFinishProcess] = useState(false)
  const [useProcess, setUseProcess] = useState<number | number[]>()
  const [allProcessSelect, setAllProcessSelect] = useState([] as any)
  const [activeInventories, setActiveInventories] = useState<ActiveInventory[]>([])

  const getInventoryWarning = (inventory: ActiveInventory): string | null => {
    const availablePercent = inventory.isUnlimited || inventory.credits <= 0
      ? 100
      : (inventory.creditsAvailable / inventory.credits) * 100
    const daysToExpiration = dayjs(inventory.expirationDate).startOf('day').diff(dayjs().startOf('day'), 'day')

    if (!inventory.isUnlimited && availablePercent <= 10) {
      return `El inventario ${inventory.name} tiene el 10% o menos disponible.`
    }

    if (daysToExpiration >= 0 && daysToExpiration <= 30) {
      return `El inventario ${inventory.name} vence en ${daysToExpiration} días.`
    }

    if (!inventory.isUnlimited && availablePercent <= 30) {
      return `El inventario ${inventory.name} tiene el 30% o menos disponible.`
    }

    if (daysToExpiration > 30 && daysToExpiration <= 90) {
      return `El inventario ${inventory.name} vence en menos de 3 meses.`
    }

    return null
  }

  useEffect(() => {
    if (process === '') {
      fetchAllProcesses()
      fetchTestsByCompany(process)
    } else {
      fetchTestsByCompany(process)
    }
  }, [process, fetchAllProcesses, fetchTestsByCompany])

  useEffect(() => {
    const processIDs = allProcess?.map((processFilter) => processFilter.value)
    const allProcessFiltered = allProcess?.filter(
      (item: any, index, self) => index === self.findIndex((ts: any) => ts.process_id === item.process_id)
    )
    setAllProcessSelect(allProcessFiltered)
    const numberProcessIDs = processIDs?.map((id) => Number(id))
    const processToSend = !process ? numberProcessIDs : process

    if (processIDs?.length === 0 || !processToSend) {
      return
    }
    setUseProcess(processToSend)
    fetchProcessData(processToSend)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProcess, fetchProcessData, process])

  useEffect(() => {
    if (role !== 'super_platform_admin' || !companyId) {
      setActiveInventories([])
      return
    }

    fetchActiveInventories(companyId).then(setActiveInventories)
  }, [companyId, role])

  const handleCreateProcess = () => {
    dispatch(cleanProcess())
    setTimeout(() => {
      navigate(platformAdminRoutes.CreateProcess)
    }, 1000)
  }

  const onFinishProcess = useCallback(
    (processId: number) => {
      if (!loadingFinishProcess) {
        setLoadingFinishProcess(true)
        getProcessDetail(processId)
          .then((response: any) => {
            setLoadingFinishProcess(false)
            handleFinishProcess(response.process)
          })
          .catch(() => {
            getError('Ha ocurrido un error al cargar este proceso')
            setLoadingFinishProcess(false)
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getError]
  )

  const handleSelectChange = (e: any) => {
    if (e) {
      setProcess(parseInt(e))
    } else {
      setProcess('')
    }
  }

  const handleSelectClick = () => {
    if (allProcess.length === 0) {
      getWarning('No tienes procesos asignados')
    }
  }

  let processesToShow: Array<{
    process_id: number
    process_name: string
    active_process: boolean
    testId: number
    test_name: string
  }> = []

  if (processInfo && processInfo.all_processes.length > 0) {
    processesToShow = _.uniqBy(processInfo.all_processes, 'process_id')
    processesToShow = processesToShow.slice(0, 10)
  }

  const renderLabels = () => {
    const labels = [
      `Betesa (${testsByCompany?.categories.Betesa || 0})`,
      `${t('global.common.skillText')} (${testsByCompany?.categories.Skills || 0})`,
      `${t('global.common.threeSixtyText')} (${testsByCompany?.categories.Test360 || 0})`,
    ]
    if (process === '' || process === 0) {
      labels.push(`Pruebas en borrador (${testsByCompany?.draftTests})`)
    }
    return labels
  }

  return (
    <MainLayout>
      {loadingAllProcesses || loadingDetailProcess ? (
        <ModalLoader isLoading={loadingAllProcesses || loadingDetailProcess} />
      ) : (
        <Container>
          <TopContainer>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16, width: 470, alignItems: 'center' }}>
              <SubtitleBold>Visualizar:</SubtitleBold>
              <Select
                label="Todos los procesos"
                options={allProcessSelect || []}
                value={process}
                onChange={(e) => handleSelectChange(e)}
                onClick={() => handleSelectClick()}
              />
            </div>
            <Button onClick={handleCreateProcess} type="submit">
              Nuevo proceso
            </Button>
          </TopContainer>
          {role === 'super_platform_admin' && (
            <InventoryListContainer>
              <InventoryHeader>
                <Subtitle>Inventarios activos</Subtitle>
                <InventoryAction
                  to={platformAdminRoutes.Inventory.replace(':companyId', companyId.toString())}
                  state={process ? { processSelectedId: process } : {}}
                >
                  Ver inventarios <span aria-hidden="true">→</span>
                </InventoryAction>
              </InventoryHeader>
              <InventoryList>
                {activeInventories.length > 0 ? (
                  activeInventories.map((inventory) => (
                    (() => {
                      const warning = getInventoryWarning(inventory)

                      return (
                        <InventoryItem key={inventory.id}>
                          <InventoryItemLabel>{inventory.name}</InventoryItemLabel>
                          <InventoryItemValue>
                            Disponibles: {inventory.isUnlimited ? 'Ilimitado' : inventory.creditsAvailable}
                          </InventoryItemValue>
                          <InventoryItemValue>
                            Utilizados: {inventory.usedCredits}
                          </InventoryItemValue>
                          <InventoryItemValue>
                            Vence: {dayjs(inventory.expirationDate).format('DD/MM/YYYY')}
                          </InventoryItemValue>
                          {warning && <InventoryWarning>{warning}</InventoryWarning>}
                        </InventoryItem>
                      )
                    })()
                  ))
                ) : (
                  <InventoryItemValue>No hay inventarios activos.</InventoryItemValue>
                )}
              </InventoryList>
            </InventoryListContainer>
          )}
          <BigContainer>
            <BigCard>
              <div style={{ zIndex: 3 }}>
                <Subtitle>Resumen de pruebas</Subtitle>
              </div>
              {processInfo && (
                <ChartContainer>
                  <Doughnut
                    dataset={[
                      testsByCompany?.categories.Betesa || 0,
                      testsByCompany?.categories.Skills || 0,
                      testsByCompany?.categories.Test360 || 0,
                      testsByCompany?.draftTests || 0,
                    ]}
                    type="summary"
                    labels={renderLabels()}
                    totalTests={testsByCompany?.totalTests || 0}
                  />
                </ChartContainer>
              )}
            </BigCard>
            <BigCard>
              <div style={{ zIndex: 3 }}>
                <Subtitle>
                  Pruebas totales contestadas vs. total
                </Subtitle>
              </div>
              {processInfo && (
                <ChartContainer>
                  <Doughnut
                    dataset={[processInfo?.total_tests_generated.total, processInfo?.total_tests_completed]}
                    type="comparison"
                    labels={[
                      `Pruebas generadas (${testsByCompany?.totalTests})`,
                      `Pruebas contestadas (${processInfo?.total_tests_completed})`,
                    ]}
                    totalTests={processInfo?.total_tests_completed}
                    firstColor="#2D749C"
                    secondColor="#C4CDD5"
                  />
                </ChartContainer>
              )}
            </BigCard>

            <MyProcessContainer>
              <Subtitle>
                Mis procesos
              </Subtitle>
              <MyProcessList>
              {processInfo && processInfo.all_processes.length > 0 ? (
                <div
                  style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%' }}
                >
                  {_.uniqBy(processesToShow, 'process_id').map((item, indexItem) => {
                    const Component = item.active_process ? ItemSendedProcess : ItemDraftProcesses
                    return (
                      <Component
                        onFinishProcess={onFinishProcess}
                        processId={item.process_id}
                        name={item.process_name}
                        key={`${indexItem + item.process_name}`}
                      />
                    )
                  })}
                </div>
              ) : (
                <P>No existen procesos asociados a esta compañía.</P>
              )}
              </MyProcessList>
              <MyProcessBottomContainer>
                <Link to={platformAdminRoutes.MyProcess} state={useProcess}>
                  <Button type="submit">Ver más</Button>
                </Link>
              </MyProcessBottomContainer>
            </MyProcessContainer>
          </BigContainer>
        </Container>
      )}
    </MainLayout>
  )
}

export default HomePlatformAdministrator
